import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { legalApi } from '../../services/api';
import { Shield, X, BookOpen, Loader2, FileText, Cookie, ShieldCheck, Scale, Search, Fingerprint, Bot } from 'lucide-react';

const LegalDocumentsModal = ({
  isOpen,
  onClose,
  initialSlug = null,
  language = 'en',
  isRTL = false
}) => {
  const [legalDocs, setLegalDocs] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Static fallback documents (used when API is unavailable or empty)
  const staticLegalDocs = [
    {
      slug: 'privacy-policy',
      category: 'Policy',
      title_en: 'Privacy Policy',
      title_ar: 'سياسة الخصوصية',
      title_tr: 'Gizlilik Politikası',
      content_en: `## Privacy Policy\n\nYour privacy is important to us. This Privacy Policy explains how we collect, use, and protect your personal information when you use CVLogs.\n\n### 1. Information We Collect\nWe may collect personal data such as your name, email address, and professional history when you create an account.\n\n### 2. How We Use Your Data\nYour data is used to generate optimized resumes, improve our AI services, and personalize your experience.\n\n### 3. Data Protection\nWe implement industry-standard security measures to ensure your data is safe and not shared with unauthorized third parties.\n\n*Last updated: June 2026*`,
      content_ar: `## سياسة الخصوصية\n\nخصوصيتك تهمنا. توضح سياسة الخصوصية هذه كيفية جمع واستخدام وحماية معلوماتك الشخصية عند استخدامك لـ CVLogs.\n\n### 1. المعلومات التي نجمعها\nقد نقوم بجمع بيانات شخصية مثل اسمك وعنوان بريدك الإلكتروني وتاريخك المهني عند إنشاء حساب.\n\n### 2. كيف نستخدم بياناتك\nتُستخدم بياناتك لإنشاء سير ذاتية مُحسنة، وتطوير خدمات الذكاء الاصطناعي الخاصة بنا، وتخصيص تجربتك.\n\n### 3. حماية البيانات\nنحن نطبق معايير أمان معتمدة لضمان أمان بياناتك وعدم مشاركتها مع أطراف ثالثة غير مصرح لها.\n\n*آخر تحديث: يونيو 2026*`,
      content_tr: `## Gizlilik Politikası\n\nGizliliğiniz bizim için önemlidir. Bu Gizlilik Politikası, CVLogs'u kullandığınızda kişisel bilgilerinizi nasıl topladığımızı, kullandığımızı ve koruduğumuzu açıklar.\n\n### 1. Topladığımız Bilgiler\nBir hesap oluşturduğunuzda adınız, e-posta adresiniz ve mesleki geçmişiniz gibi kişisel verileri toplayabiliriz.\n\n### 2. Verilerinizi Nasıl Kullanıyoruz\nVerileriniz, optimize edilmiş özgeçmişler oluşturmak, yapay zeka hizmetlerimizi geliştirmek ve deneyiminizi kişiselleştirmek için kullanılır.\n\n### 3. Veri Koruması\nVerilerinizin güvende olmasını ve yetkisiz üçüncü şahıslarla paylaşılmamasını sağlamak için endüstri standardında güvenlik önlemleri uyguluyoruz.\n\n*Son güncelleme: Haziran 2026*`,
      updated_at: '2026-06-01T00:00:00Z'
    },
    {
      slug: 'terms-of-service',
      category: 'Terms',
      title_en: 'Terms of Service',
      title_ar: 'شروط الخدمة',
      title_tr: 'Hizmet Şartları',
      content_en: `## Terms of Service\n\nWelcome to CVLogs. By accessing our website, you agree to comply with these terms.\n\n### 1. User Account\nYou must provide accurate information when creating your account. You are responsible for safeguarding your login credentials.\n\n### 2. Acceptable Use\nYou agree not to misuse our AI services or attempt to access our systems without authorization.\n\n### 3. Service Availability\nWe strive to keep the service running 24/7, but we do not guarantee uninterrupted access and may perform maintenance occasionally.\n\n*Last updated: June 2026*`,
      content_ar: `## شروط الخدمة\n\nمرحباً بك في CVLogs. بدخولك إلى موقعنا، فإنك توافق على الالتزام بهذه الشروط.\n\n### 1. حساب المستخدم\nيجب عليك تقديم معلومات دقيقة عند إنشاء حسابك. أنت مسؤول عن حماية بيانات الدخول الخاصة بك.\n\n### 2. الاستخدام المقبول\nأنت توافق على عدم إساءة استخدام خدمات الذكاء الاصطناعي الخاصة بنا أو محاولة الوصول إلى أنظمتنا دون تصريح.\n\n### 3. توافر الخدمة\nنحن نسعى جاهدين للحفاظ على تشغيل الخدمة على مدار الساعة، ولكننا لا نضمن الوصول غير المنقطع وقد نقوم بإجراء صيانة من حين لآخر.\n\n*آخر تحديث: يونيو 2026*`,
      content_tr: `## Hizmet Şartları\n\nCVLogs'a hoş geldiniz. Web sitemize erişerek, bu şartlara uymayı kabul edersiniz.\n\n### 1. Kullanıcı Hesabı\nHesabınızı oluştururken doğru bilgiler vermelisiniz. Giriş bilgilerinizin güvenliğinden siz sorumlusunuz.\n\n### 2. Kabul Edilebilir Kullanım\nYapay zeka hizmetlerimizi kötüye kullanmamayı veya sistemlerimize yetkisiz erişim sağlamaya çalışmamayı kabul edersiniz.\n\n### 3. Hizmetin Kullanılabilirliği\nHizmeti 7/24 çalışır durumda tutmaya çalışıyoruz, ancak kesintisiz erişim garantisi vermiyoruz ve zaman zaman bakım yapabiliriz.\n\n*Son güncelleme: Haziran 2026*`,
      updated_at: '2026-06-01T00:00:00Z'
    },
    {
      slug: 'refund-policy',
      category: 'Policy',
      title_en: 'Refund Policy',
      title_ar: 'سياسة الاسترداد',
      title_tr: 'İade Politikası',
      content_en: `## Refund Policy\n\nAll purchases made on CVLogs are **final and non-refundable**.\n\nBy subscribing to any paid plan, you acknowledge and agree that:\n\n- No refunds will be issued once payment has been processed.\n- You may cancel your subscription at any time, but no partial or prorated refunds will be provided for the remaining billing period.\n- Free trial periods, if offered, allow you to evaluate the service before committing to a paid plan.\n\nIf you experience a technical issue that prevents you from using the service, please contact us at **contact@cvlogs.com** and we will do our best to resolve the issue.\n\n*Last updated: June 2026*`,
      content_ar: `## سياسة الاسترداد\n\nجميع عمليات الشراء على CVLogs **نهائية وغير قابلة للاسترداد**.\n\nبالاشتراك في أي خطة مدفوعة، فإنك تقر وتوافق على أن:\n\n- لن يتم إصدار أي استرداد بعد معالجة الدفع.\n- يمكنك إلغاء اشتراكك في أي وقت، ولكن لن يتم تقديم أي استرداد جزئي أو نسبي عن فترة الفوترة المتبقية.\n- فترات التجربة المجانية، إذا تم تقديمها، تتيح لك تقييم الخدمة قبل الالتزام بخطة مدفوعة.\n\nإذا واجهت مشكلة تقنية تمنعك من استخدام الخدمة، يرجى التواصل معنا على **contact@cvlogs.com** وسنبذل قصارى جهدنا لحل المشكلة.\n\n*آخر تحديث: يونيو 2026*`,
      content_tr: `## İade Politikası\n\nCVLogs'ta yapılan tüm satın alımlar **nihai ve iade edilemez** niteliktedir.\n\nHerhangi bir ücretli plana abone olarak şunları kabul ve beyan edersiniz:\n\n- Ödeme işlemi tamamlandıktan sonra iade yapılmaz.\n- Aboneliğinizi istediğiniz zaman iptal edebilirsiniz ancak kalan faturalandırma dönemi için kısmi veya orantılı bir iade yapılmayacaktır.\n- Ücretsiz deneme süreleri (sunulursa), ücretli bir plana geçmeden önce hizmeti değerlendirmenize olanak tanır.\n\nHizmeti kullanmanızı engelleyen teknik bir sorun yaşarsanız, lütfen **contact@cvlogs.com** adresinden bizimle iletişime geçin; sorunu çözmek için elimizden geleni yapacağız.\n\n*Son güncelleme: Haziran 2026*`,
      updated_at: '2026-06-01T00:00:00Z'
    },
    {
      slug: 'cookie-policy',
      category: 'Policy',
      title_en: 'Cookie Policy',
      title_ar: 'ملفات تعريف الارتباط',
      title_tr: 'Çerez Politikası',
      content_en: `## Cookie Policy\n\nWe use cookies to enhance your browsing experience, analyze site traffic, and personalize content.\n\n### What are cookies?\nCookies are small files stored on your device that help our website function properly.\n\n### Managing Cookies\nYou can control or delete cookies through your browser settings. However, disabling essential cookies may affect site functionality.\n\n*Last updated: June 2026*`,
      content_ar: `## ملفات تعريف الارتباط\n\nنستخدم ملفات تعريف الارتباط لتحسين تجربة التصفح الخاصة بك، وتحليل حركة المرور على الموقع، وتخصيص المحتوى.\n\n### ما هي ملفات تعريف الارتباط؟\nملفات تعريف الارتباط هي ملفات صغيرة تُحفظ على جهازك لتساعد موقعنا على العمل بشكل صحيح.\n\n### إدارة ملفات تعريف الارتباط\nيمكنك التحكم في ملفات تعريف الارتباط أو حذفها من خلال إعدادات متصفحك. ومع ذلك، قد يؤدي تعطيل ملفات تعريف الارتباط الأساسية إلى التأثير على وظائف الموقع.\n\n*آخر تحديث: يونيو 2026*`,
      content_tr: `## Çerez Politikası\n\nTarama deneyiminizi geliştirmek, site trafiğini analiz etmek ve içeriği kişiselleştirmek için çerezler kullanıyoruz.\n\n### Çerezler nedir?\nÇerezler, web sitemizin düzgün çalışmasına yardımcı olan ve cihazınızda saklanan küçük dosyalardır.\n\n### Çerezleri Yönetme\nTarayıcı ayarlarınız aracılığıyla çerezleri kontrol edebilir veya silebilirsiniz. Ancak, temel çerezleri devre dışı bırakmak site işlevselliğini etkileyebilir.\n\n*Son güncelleme: Haziran 2026*`,
      updated_at: '2026-06-01T00:00:00Z'
    },
    {
      slug: 'dpa',
      category: 'Legal',
      title_en: 'Data Processing Agreement (DPA)',
      title_ar: 'اتفاقية معالجة البيانات (DPA)',
      title_tr: 'Veri İşleme Sözleşmesi (DPA)',
      content_en: `## Data Processing Agreement\n\nThis Data Processing Agreement details how CVLogs processes personal data on behalf of our users in compliance with GDPR and other privacy laws.\n\n### Scope of Processing\nWe process user data solely to provide the CV generation services as requested by the user.\n\n### Subprocessors\nWe may use trusted third-party services to host data, process payments, or run AI algorithms, ensuring they meet strict security standards.\n\n*Last updated: June 2026*`,
      content_ar: `## اتفاقية معالجة البيانات\n\nتوضح اتفاقية معالجة البيانات هذه كيفية قيام CVLogs بمعالجة البيانات الشخصية نيابة عن مستخدمينا وفقاً للائحة العامة لحماية البيانات (GDPR) وغيرها من قوانين الخصوصية.\n\n### نطاق المعالجة\nنقوم بمعالجة بيانات المستخدمين حصرياً لتقديم خدمات إنشاء السير الذاتية كما يطلبها المستخدم.\n\n### المعالجون الفرعيون\nقد نستخدم خدمات أطراف ثالثة موثوقة لاستضافة البيانات أو معالجة المدفوعات أو تشغيل خوارزميات الذكاء الاصطناعي، مع ضمان استيفائهم لمعايير أمنية صارمة.\n\n*آخر تحديث: يونيو 2026*`,
      content_tr: `## Veri İşleme Sözleşmesi\n\nBu Veri İşleme Sözleşmesi, CVLogs'un GDPR ve diğer gizlilik yasalarına uygun olarak kullanıcılarımız adına kişisel verileri nasıl işlediğini detaylandırmaktadır.\n\n### İşleme Kapsamı\nKullanıcı verilerini yalnızca kullanıcı tarafından talep edilen özgeçmiş oluşturma hizmetlerini sağlamak için işliyoruz.\n\n### Alt İşleyiciler\nVerileri barındırmak, ödemeleri işlemek veya AI algoritmalarını çalıştırmak için güvenilir üçüncü taraf hizmetleri kullanabiliriz, bu hizmetlerin katı güvenlik standartlarını karşıladığından emin oluruz.\n\n*Son güncelleme: Haziran 2026*`,
      updated_at: '2026-06-01T00:00:00Z'
    },
    {
      slug: 'ai-policy',
      category: 'Policy',
      title_en: 'AI Usage Policy',
      title_ar: 'سياسة الذكاء الاصطناعي',
      title_tr: 'Yapay Zeka Politikası',
      content_en: `## AI Usage Policy\n\nOur platform utilizes Artificial Intelligence to enhance and optimize resumes.\n\n### 1. Fair Use\nUsers must not exploit AI generation tools to create misleading or fraudulent information.\n\n### 2. Privacy in AI Processing\nYour data is processed securely by our AI models and is not used to train public models without your explicit consent.\n\n*Last updated: June 2026*`,
      content_ar: `## سياسة الذكاء الاصطناعي\n\nتستخدم منصتنا الذكاء الاصطناعي لتحسين وتطوير السير الذاتية.\n\n### 1. الاستخدام العادل\nيجب على المستخدمين عدم استغلال أدوات التوليد بالذكاء الاصطناعي لإنشاء معلومات مضللة أو احتيالية في السيرة الذاتية.\n\n### 2. الخصوصية في معالجة البيانات\nتتم معالجة بياناتك بشكل آمن بواسطة نماذج الذكاء الاصطناعي الخاصة بنا، ولا تُستخدم لتدريب النماذج العامة دون موافقتك الصريحة.\n\n*آخر تحديث: يونيو 2026*`,
      content_tr: `## Yapay Zeka Politikası\n\nPlatformumuz, özgeçmişleri geliştirmek ve optimize etmek için Yapay Zekadan yararlanmaktadır.\n\n### 1. Adil Kullanım\nKullanıcılar, yanıltıcı veya sahte bilgiler oluşturmak için yapay zeka üretim araçlarını kötüye kullanmamalıdır.\n\n### 2. Yapay Zeka İşlemede Gizlilik\nVerileriniz yapay zeka modellerimiz tarafından güvenli bir şekilde işlenir ve açık izniniz olmadan genel modelleri eğitmek için kullanılmaz.\n\n*Son güncelleme: Haziran 2026*`,
      updated_at: '2026-06-01T00:00:00Z'
    }
  ];

  // Load documents whenever the modal opens or language changes
  useEffect(() => {
    if (!isOpen) return;

    const fetchDocuments = async () => {
      try {
        setIsLoading(true);
        setError(null);
        let docs = [];
        try {
          const response = await legalApi.listDocuments();
          docs = response?.documents || [];
        } catch (e) {
          console.warn('API fetch failed, falling back to static docs', e);
        }

        // Merge fetched docs with static docs (preferring fetched docs if available)
        const allDocs = staticLegalDocs.map(staticDoc => {
          const fetchedDoc = docs.find(d => d.slug === staticDoc.slug);
          return fetchedDoc || staticDoc;
        });

        // Add any extra fetched docs that are not in static list
        docs.forEach(fetchedDoc => {
          if (!allDocs.some(d => d.slug === fetchedDoc.slug)) {
            allDocs.push(fetchedDoc);
          }
        });

        setLegalDocs(allDocs);

        const fallbackSlug = allDocs[0]?.slug || null;
        setActiveSlug((prev) => {
          if (initialSlug && allDocs.some((doc) => doc.slug === initialSlug)) {
            return initialSlug;
          }
          if (prev && allDocs.some((doc) => doc.slug === prev)) {
            return prev;
          }
          return fallbackSlug;
        });
      } catch (err) {
        console.error('Failed to load legal documents', err);
        setError(language === 'tr' ? 'Yasal belgeler yüklenemedi.' : language === 'ar' ? 'تعذر تحميل المستندات القانونية.' : 'Unable to load legal documents.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [isOpen, language, initialSlug]);

  // Ensure a document exists locally (fetch detail if necessary)
  const ensureDocument = async (slug) => {
    const exists = legalDocs.some((doc) => doc.slug === slug && doc.content_en);
    if (exists) return;

    try {
      setIsLoading(true);
      const fetchedDoc = await legalApi.getDocument(slug);
      if (fetchedDoc) {
        setLegalDocs((prev) => {
          const duplicate = prev.some((doc) => doc.slug === fetchedDoc.slug);
          return duplicate ? prev.map((doc) => (doc.slug === fetchedDoc.slug ? fetchedDoc : doc)) : [...prev, fetchedDoc];
        });
      }
    } catch (err) {
      console.error('Failed to load document', err);
      setError(language === 'tr' ? 'Bu belge şu anda kullanılamıyor.' : language === 'ar' ? 'هذا المستند غير متوفر حالياً.' : 'This document is currently unavailable.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectDocument = async (slug) => {
    if (!slug) return;
    await ensureDocument(slug);
    setActiveSlug(slug);
  };

  useEffect(() => {
    if (!initialSlug || !legalDocs.length) return;
    if (legalDocs.some((doc) => doc.slug === initialSlug)) {
      setActiveSlug(initialSlug);
    }
  }, [initialSlug, legalDocs]);

  const activeDoc = useMemo(() => legalDocs.find((doc) => doc.slug === activeSlug), [legalDocs, activeSlug]);

  const filteredDocs = useMemo(() => {
    if (!searchQuery) return legalDocs;
    const lowerQuery = searchQuery.toLowerCase();
    return legalDocs.filter(doc => {
      const titleEn = (doc.title_en || '').toLowerCase();
      const titleAr = (doc.title_ar || '').toLowerCase();
      const titleTr = (doc.title_tr || '').toLowerCase();
      return titleEn.includes(lowerQuery) || titleAr.includes(lowerQuery) || titleTr.includes(lowerQuery);
    });
  }, [legalDocs, searchQuery]);

  const getIconForSlug = (slug) => {
    if (slug.includes('privacy')) return <ShieldCheck className="w-4 h-4" />;
    if (slug.includes('terms')) return <Scale className="w-4 h-4" />;
    if (slug.includes('cookie')) return <Cookie className="w-4 h-4" />;
    if (slug.includes('dpa')) return <Fingerprint className="w-4 h-4" />;
    if (slug.includes('ai')) return <Bot className="w-4 h-4" />;
    return <FileText className="w-4 h-4" />;
  };

  const getLocalizedField = (doc, field) => {
    if (!doc) return '';
    const suffix = language === 'tr' ? 'tr' : language === 'ar' ? 'ar' : 'en';
    return doc[`${field}_${suffix}`] || doc[`${field}_en`] || '';
  };

  const formatDate = (isoDate) => {
    if (!isoDate) return '';
    return new Date(isoDate).toLocaleDateString(language === 'tr' ? 'tr-TR' : language === 'ar' ? 'ar-EG' : 'en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const markdownComponents = {
    h1: ({ node, ...props }) => <h2 className="text-3xl font-bold text-slate-900 mt-8 mb-4" {...props} />,
    h2: ({ node, ...props }) => <h3 className="text-2xl font-semibold text-slate-900 mt-6 mb-3" {...props} />,
    h3: ({ node, ...props }) => <h4 className="text-xl font-semibold text-slate-800 mt-4 mb-2" {...props} />,
    p: ({ node, ...props }) => <p className="text-slate-700 leading-relaxed mb-4" {...props} />,
    ul: ({ node, ...props }) => <ul className="list-disc pl-5 text-slate-700 space-y-2 mb-4" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal pl-5 text-slate-700 space-y-2 mb-4" {...props} />,
    li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
    strong: ({ node, ...props }) => <strong className="text-slate-900 font-semibold" {...props} />,
    a: ({ node, ...props }) => <a className="text-blue-600 underline" target="_blank" rel="noreferrer" {...props} />
  };

  if (!isOpen) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-md px-4 py-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-5xl rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh]"
        >
          <div className={`flex items-center justify-between px-6 py-4 border-b border-slate-100`}>
            <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-blue-600">
                <Shield className="w-4 h-4" />
                {language === 'tr' ? 'Yasal Belgeler' : language === 'ar' ? 'الوثائق القانونية' : 'Legal Documents'}
              </p>
              <h2 className="text-2xl font-black text-slate-900 mt-1">
                {language === 'tr' ? 'CVLogs Yasal Merkezi' : language === 'ar' ? 'المركز القانوني لـ CVLogs' : 'CVLogs Legal Center'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
              aria-label={language === 'tr' ? 'Kapat' : language === 'ar' ? 'إغلاق' : 'Close'}
            >
              <X className="w-6 h-6 text-slate-500" />
            </button>
          </div>

          <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-[60vh]">
            {/* Sidebar Navigation */}
            <div className={`w-full md:w-64 bg-slate-50/80 border-slate-100 flex-shrink-0 flex flex-col ${isRTL ? 'border-l md:border-b-0 border-b' : 'border-r md:border-b-0 border-b'}`}>
              <div className="p-4 border-b border-slate-100 bg-white/50">
                <div className="relative mb-2">
                  <Search className={`w-4 h-4 text-slate-400 absolute top-1/2 -translate-y-1/2 ${isRTL ? 'right-3' : 'left-3'}`} />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={language === 'tr' ? 'Belge ara...' : language === 'ar' ? 'البحث عن مستند...' : 'Search documents...'}
                    className={`w-full bg-slate-100 border-none rounded-xl py-2.5 text-sm focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all ${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'}`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-3 space-y-1 custom-scrollbar">
                {filteredDocs.length > 0 ? (
                  filteredDocs.map((doc) => (
                    <button
                      key={doc.slug}
                      type="button"
                      onClick={() => handleSelectDocument(doc.slug)}
                      className={`w-full text-sm font-medium px-4 py-3.5 rounded-xl transition-all duration-200 flex items-center gap-3 ${isRTL ? 'text-right flex-row-reverse' : 'text-left flex-row'} ${
                        activeSlug === doc.slug
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                          : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm border border-transparent hover:border-slate-200'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${activeSlug === doc.slug ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>
                        {getIconForSlug(doc.slug)}
                      </div>
                      <span className="flex-1 truncate">{getLocalizedField(doc, 'title') || doc.slug}</span>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-8 text-sm text-slate-400">
                    {language === 'tr' ? 'Sonuç bulunamadı' : language === 'ar' ? 'لا توجد نتائج' : 'No results found'}
                  </div>
                )}
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 md:p-12 bg-white relative custom-scrollbar">
              {isLoading ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-blue-600 bg-white/80 backdrop-blur-sm z-10">
                  <Loader2 className="w-10 h-10 animate-spin" />
                  <p className="font-semibold">
                    {language === 'tr' ? 'Belge yükleniyor...' : language === 'ar' ? 'جارٍ تحميل المستند...' : 'Loading document...'}
                  </p>
                </div>
              ) : error ? (
                <div className="absolute inset-0 flex items-center justify-center p-6 bg-slate-50/50">
                  <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white shadow-xl shadow-red-100 p-8 text-center text-red-700">
                    <Shield className="w-12 h-12 mx-auto mb-4 text-red-400 opacity-50" />
                    <p className="font-medium text-lg">{error}</p>
                  </div>
                </div>
              ) : activeDoc ? (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeDoc.slug}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className={`${isRTL ? 'text-right' : 'text-left'} max-w-3xl mx-auto pb-10`}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <div className="mb-10 pb-6 border-b border-slate-100">
                      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-widest mb-4">
                        {getIconForSlug(activeDoc.slug)}
                        {activeDoc.category}
                      </div>
                      <h3 className="text-2xl md:text-4xl font-black mb-4 leading-tight bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-slate-600">
                        {getLocalizedField(activeDoc, 'title')}
                      </h3>
                      <div className="flex items-center gap-2 mt-4">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                        </span>
                        <p className="text-sm font-medium text-slate-500">
                          {language === 'tr' ? 'Son güncelleme:' : language === 'ar' ? 'آخر تحديث:' : 'Last updated:'} {formatDate(activeDoc.updated_at || activeDoc.published_at)}
                        </p>
                      </div>
                    </div>
                    <div className="prose prose-slate prose-blue max-w-none 
                      prose-headings:font-bold prose-headings:text-slate-900
                      prose-p:text-slate-600 prose-p:leading-relaxed
                      prose-a:text-blue-600 hover:prose-a:text-blue-700
                      prose-strong:text-slate-900 prose-strong:font-semibold
                      prose-li:text-slate-600 prose-ul:space-y-2
                    ">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={markdownComponents}
                      >
                        {getLocalizedField(activeDoc, 'content')}
                      </ReactMarkdown>
                    </div>
                  </motion.div>
                </AnimatePresence>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
                  <BookOpen className="w-16 h-16 mb-4 opacity-20" />
                  <p className="font-medium">
                    {language === 'tr' ? 'Mevcut belge yok.' : language === 'ar' ? 'لا توجد مستندات متاحة.' : 'No documents available.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LegalDocumentsModal;

